import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DocumentUploadParams } from '@gov-nx/api';
import { fileShapeNative, fileShapeWeb } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Optional } from '@gov-nx/core/types';
import type { PageCode } from '@gov-nx/module/page';
import { MyFilesSource } from '@gov-nx/module/page';
import type { MyFilesUploadFormData } from './context.types';

export interface FormInstanceProps {
	code: PageCode;
}

export function FormInstanceNative({
	code,
}: FormInstanceProps): FormDefinition<MyFilesUploadFormData> {
	const { t } = useLocale(code);
	const formSchema = yup
		.object({
			files: yup
				.array()
				.of(fileShapeNative())
				.compact((value) => !value.id)
				.min(1, t('soubor.validace.povinne')),
			name: yup.string().required(t('nazev-dokumentu.validace.povinne')),
		})
		.required();

	const formMethods = useGovForm<MyFilesUploadFormData>({
		formSchema,
		defaultValues: {
			files: [],
			name: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export function FormInstanceWeb({
	code,
}: FormInstanceProps): FormDefinition<MyFilesUploadFormData> {
	const { t } = useLocale(code);
	const formSchema = yup
		.object({
			files: yup
				.array()
				.of(fileShapeWeb())
				.compact((value) => !value.id)
				.min(1, t('soubor.validace.povinne')),
			name: yup.string().required(t('nazev-dokumentu.validace.povinne')),
		})
		.required();

	const formMethods = useGovForm<MyFilesUploadFormData>({
		formSchema,
		defaultValues: {
			files: [],
			name: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (
	data: MyFilesUploadFormData
): Optional<DocumentUploadParams> => {
	const soubor = prepareAppendPhotosData(data);
	if (!soubor) return;
	return {
		zdroj: MyFilesSource.UZIVATELEM_NAHRANY_DOKUMENT,
		nazev: data.name,
		soubory: [...(soubor ? [soubor] : [])],
	};
};

export const prepareAppendPhotosData = (
	data: MyFilesUploadFormData
): { souborId: string; nazev: string } | null => {
	if (data.files?.[0].fileId && data.files?.[0].name) {
		return {
			souborId: data.files?.[0].fileId,
			nazev: data.files?.[0].name,
		};
	}
	return null;
};
